import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { ToastService } from '@libs/src/toast/toast.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { Memoize } from '@libs/src/functions/memoize';
import { Account } from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import {
  ProgramMembership,
  ProgramMembershipRole,
  programMembershipStatus,
  ProgramMembershipStatus,
  ProgramRole,
  programRoleMentee,
  programRoleMentor,
} from '@libs/src/models/program-membership.model';
import {
  getProgramRoles,
  isUserBothMentorMentee,
} from '@libs/src/program-memberships/program-membership.helper';

import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';

type OnChangeFn = (value: ProgramMembership['status'] | string) => void;
type OnTouchedFn = () => void;

@Component({
  providers: [
    DestroySubscriptionsService,
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserOnboardingProgramFormComponent),
    },
  ],
  selector: 'app-user-onboarding-program-form',
  templateUrl: './user-onboarding-program-form.component.html',
})
export class UserOnboardingProgramFormComponent
  implements ControlValueAccessor, OnChanges, OnInit
{
  @Input() programMembership: ProgramMembership;
  @Input() user: Account;
  @Input({ required: true }) group: Group;
  readonly programRoleMentor = programRoleMentor;
  readonly programRoleMentee = programRoleMentee;
  readonly programMembershipStatusActive = programMembershipStatus.active;
  readonly programMembershipStatusDeclined = programMembershipStatus.declined;
  programPrimaryRole: string;
  programSecondaryRole: string;
  isLoading = false;
  formSubmissionError$ = new Subject<void>();
  preferencesForm: FormControl<{
    capacity: number;
    preferred_matches: string[];
    roles: string;
  }> = new FormControl();
  programRoles: ProgramMembershipRole[];
  private _isEditMode = false;

  set isEditMode(mode: boolean) {
    this._isEditMode = mode;
    this.emitValue();
  }

  get isEditMode() {
    return this._isEditMode;
  }

  onChange: OnChangeFn = () => {};

  registerOnChange(onChangeFunction: OnChangeFn): void {
    this.onChange = onChangeFunction;
  }

  onTouched: OnTouchedFn = () => {};

  registerOnTouched(onTouchedFunction: OnTouchedFn): void {
    this.onTouched = onTouchedFunction;
  }

  constructor(
    private readonly accountBackend: AccountBackend,
    private readonly cd: ChangeDetectorRef,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastService: ToastService,
  ) {}

  get isRoleSelectionEnabled() {
    return (
      this.programMembership.program?.user_preferences_permissions || []
    ).includes('roles');
  }

  ngOnInit() {
    this.programPrimaryRole =
      this.programMembership.program.program_config?.primary_name ||
      programRoleMentor;
    this.programSecondaryRole =
      this.programMembership.program.program_config?.secondary_name ||
      programRoleMentee;
    const isEditModeStatuses: Array<ProgramMembership['status']> = [
      programMembershipStatus.invited,
      programMembershipStatus.ready_to_invite,
      programMembershipStatus.pending,
    ];
    this.isEditMode = isEditModeStatuses.includes(
      this.programMembership.status,
    );
    this.preferencesForm.valueChanges
      .pipe(
        tap(() => {
          this.emitValue();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  emitValue() {
    if (this.isEditMode) {
      this.onChange('edit');
    } else {
      this.onChange(this.programMembership.status);
    }
  }

  writeValue(value: string) {
    this.onChange(value);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.programMembership) {
      this.programRoles = getProgramRoles(this.programMembership.program);
    }
  }

  @Memoize()
  isBothRoleOptionSelected(programMembership: ProgramMembership) {
    return isUserBothMentorMentee(programMembership);
  }

  saveProgramMembership(
    form: UserOnboardingProgramFormComponent['preferencesForm'],
    programMembershipStatus: ProgramMembershipStatus,
  ) {
    if (
      form.invalid &&
      this.programMembershipStatusActive === programMembershipStatus
    ) {
      this.formSubmissionError$.next();
      return;
    }
    this.isLoading = true;
    const formValue = form.value;
    const programPreferencesUpdate = {
      ...omit(formValue, 'roles'),
      program_roles: (formValue.roles?.split(',') as ProgramRole[]) ?? [],
    };
    return this.accountBackend.v3
      .patchAccountGroupsProgramMembershipById(
        this.group,
        this.programMembership.program._id,
        {
          ...(this.programMembershipStatusDeclined !==
            programMembershipStatus && {
            program_preferences: programPreferencesUpdate,
          }),
          status: programMembershipStatus,
        },
      )
      .pipe(
        tap(({ programMembership }) => {
          this.programMembership = {
            ...this.programMembership,
            ...pick(programMembership, ['program_preferences', 'status']),
          };
        }),
        finalize(() => {
          this.isLoading = false;
          this.isEditMode = false;
          this.cd.markForCheck();
          this.emitValue();
          this.router.navigate(['.'], {
            queryParams: { programId: this.programMembership.program._id },
            queryParamsHandling: 'merge',
            relativeTo: this.route,
          });
        }),
        catchError((error) => {
          this.toastService.showError(getServerError(error));
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
